import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { doctrineApi } from '../api/admin';
import { messageFromOrchestratorAxiosError } from '../lib/axiosErrorMessage';
import { rememberedApplicationsListPath } from '../lib/applicationsListFilters';
import { AdminListPageHeader } from '../components/admin/adminListPageUi';

export function DoctrineConfigPage() {
  const q = useQuery({ queryKey: ['doctrines'], queryFn: () => doctrineApi.list() });
  const applicationsTo = rememberedApplicationsListPath();

  if (q.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => q.refetch()}>
            Retry
          </Button>
        }
      >
        {messageFromOrchestratorAxiosError(q.error) || 'Failed to load doctrines.'}
      </Alert>
    );
  }

  return (
    <Box>
      <AdminListPageHeader
        title="Doctrine configuration"
        subtitle="Runtime dashboards live under Applications. Links below open legacy full-screen dashboards."
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open <RouterLink to={applicationsTo}>Applications</RouterLink> for live journeys. Table shortcuts below go to
        legacy dashboards.
      </Typography>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Display name</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <TableCell>
                <RouterLink to="/employer-verification">EMPLOYER_DASHBOARD</RouterLink>
              </TableCell>
              <TableCell>
                <RouterLink to="/employer-verification">Employer Dashboard</RouterLink>
              </TableCell>
              <TableCell>v1</TableCell>
              <TableCell>
                <Chip size="small" color="success" label="Active" />
              </TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>
                <RouterLink to="/income-verification">INCOME_DASHBOARD</RouterLink>
              </TableCell>
              <TableCell>
                <RouterLink to="/income-verification">Income Verification Dashboard</RouterLink>
              </TableCell>
              <TableCell>v1</TableCell>
              <TableCell>
                <Chip size="small" color="success" label="Active" />
              </TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>
                <RouterLink to="/employer-verification/analytics">EMPLOYER_DASHBOARD_ANALYTICS</RouterLink>
              </TableCell>
              <TableCell>
                <RouterLink to="/employer-verification/analytics">EV Analytics</RouterLink>
              </TableCell>
              <TableCell>v1</TableCell>
              <TableCell>
                <Chip size="small" color="success" label="Active" />
              </TableCell>
            </TableRow>
            <TableRow hover>
              <TableCell>
                <RouterLink to="/identity-verification-dashboard">IDENTITY_VERIFICATION_DASHBOARD</RouterLink>
              </TableCell>
              <TableCell>
                <RouterLink to="/identity-verification-dashboard">Identity Verification Dashboard</RouterLink>
              </TableCell>
              <TableCell>v1</TableCell>
              <TableCell>
                <Chip size="small" color="success" label="Active" />
              </TableCell>
            </TableRow>
            {(q.data ?? []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <RouterLink to={`/doctrine-config/${row.id}`}>{row.id}</RouterLink>
                </TableCell>
                <TableCell>{row.displayName}</TableCell>
                <TableCell>v{row.version}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.isActive ? 'success' : 'default'}
                    label={row.isActive ? 'Active' : 'Inactive'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
